/** @namespace JQuery */
import { CustomTooltip } from './custom-tooltip.js';
import { isOrHasDomElement } from './utils.js';

/**
 * @param {JQuery.KeyDownEvent} e - The keydown event
 */
const anchorTagKeydownFixListener = (e) => {
    if (![' ', 'Enter'].includes(e.key)) return;
    if (!(e.currentTarget instanceof HTMLElement)) return;
    e.preventDefault();
    e.currentTarget.click();
};

// eslint-disable-next-line jsdoc/require-returns
/**
 * @param {HTMLElement} navElement - The DOM element for the nav.
 * @param {import('./stellarnav-types.ts').CommonFixedHorizontalModule} commonFixedHorizontalModule - The base fixed horizontal stellarnav module.
 * @param {import('./stellarnav-types.ts').StellarnavStrictOptions} settings - Stellarnav options.
 */
const expandShrinkFactory = (navElement, commonFixedHorizontalModule, settings) => {
    if (!['leftfixed', 'rightfixed'].includes(settings.position)) {
        throw new TypeError('ExpandShrink does not support positions other than left and right fixed.');
    }
    const $nav = $(navElement);
    const $firstUl = $nav.find('ul:first');
    const closedNavWidth = 63;
    const $body = $('body');
    const rootElement = document.querySelector(':root');
    if (!(rootElement instanceof HTMLElement)) throw new TypeError('The root element is not an html element.');
    /**
     * @param {string} variableName - The variable's name.
     * @returns {import('./stellarnav-types.ts').CssVariableApi} - The css variable with controls.
     */
    const cssVariableFactory = (variableName) => {
        const get = () => getComputedStyle(rootElement).getPropertyValue(variableName);

        const getVariableName = () => variableName;

        /** @param {string} newValue - The new value for the variable. */
        const set = (newValue) => {
            rootElement.style.setProperty(variableName, newValue);
        };
        const initialValue = get();

        const reset = () => {
            set(initialValue);
        };

        return { get, getVariableName, set, reset };
    };
    const navMenuWidth = cssVariableFactory('--expand-shrink-nav-width');
    const closedNavBodyGap = cssVariableFactory('--expand-shrink-closed-nav-gap');
    const positionAlignment = settings.position.replace('fixed', '');
    const oppPosition = positionAlignment === 'left' ? 'right' : 'left';
    let { isMenuExpanded } = settings;
    let hasMenuTempExpanded = false;
    const $elementsToHide = $nav.find('.collapseMe');
    // cspell:ignore vxtlu
    const toggleButtonHtml = `
          <li class="expand-shrink__toggle-container">
            <a class="expand-shrink__toggle nav-link" tabindex="0" role="button" aria-label="Toggle Menu">
              <svg class="expand-shrink__toggle-icon nav-list-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="none">
                <path
                  d="M3.0675 21C3.0675 21 3 20.97 3 20.9325L3 3.06C3 3.06 3.03 2.9925 3.0675 2.9925L20.9325 2.9925C20.9325 2.9925 21 3.0225 21 3.06L21 20.9325C21 20.9325 20.97 21 20.9325 21L3.06 21M3.06 22.5L20.9325 22.5C21.795 22.5 22.5 21.8025 22.5 20.9325L22.5 3.06C22.5 2.1975 21.8025 1.4925 20.9325 1.4925L3.06 1.4925C2.1975 1.4925 1.4925 2.19 1.4925 3.06L1.4925 20.9325C1.4925 21.795 2.19 22.5 3.06 22.5Z" class="ExpandCollapseIcon__Outline-sc-1vxtlu9-1 dNHHvL"></path><path d="M9.2325 7.6875L13.845 12.0375L9.315 16.3125C8.94 16.6725 8.9175 17.265 9.2775 17.64C9.63 18.015 10.2225 18.03 10.5975 17.6775L15.8475 12.7275C16.2225 12.3825 16.245 11.7975 15.9 11.4225C15.8925 11.4075 15.885 11.4 15.87 11.385C15.84 11.3475 15.81 11.31 15.7725 11.2725L10.5225 6.3225C10.3425 6.1575 10.11 6.0675 9.8775 6.0675C9.63 6.0675 9.3825 6.1725 9.195 6.3675C8.8425 6.7425 8.8575 7.335 9.2325 7.6875Z" class="ExpandCollapseIcon__InnerArrow-sc-1vxtlu9-0 cobYFX"
                ></path>
              </svg>
            </a>
          </li>
        `;
    const $disabledNavLinks = $firstUl.find('.nav-link.disabled');
    /** @type {import('./stellarnav-types.ts').ESAfterSetupVars} */
    const afterSetupVars = {};
    const $firstLevelLinks = $firstUl.find('li[data-level="1"]:not([hidden]) > a.nav-link:not(.disabled)');
    const navListDom = $firstUl.get(0);
    if (!navListDom) throw new TypeError('nav ul was not found');
    const originalAlignment = navListDom.style.getPropertyValue(positionAlignment);
    const getToggleTooltipText = () => (isMenuExpanded ? 'Collapse' : 'Expand');

    const commonSetup = () => {
        commonFixedHorizontalModule.setup();

        // Will need to have a discussion on how to handle the disabled links
        // $disabledNavLinks.get().forEach((disabledLink) => {
        //     disabledLink.dataset.disabledText = disabledLink.textContent;
        //     disabledLink.textContent = '';
        // });

        if (settings.toggleOnTop) {
            $firstUl.prepend(toggleButtonHtml);
        } else {
            $firstUl.append(toggleButtonHtml);
            $firstUl.find('.expand-shrink__toggle-container').css('margin-top', 'auto');
        }

        Object.assign(afterSetupVars, {
            $navToggle: $('.expand-shrink__toggle'),
            $toggleIcon: $nav.find('.expand-shrink__toggle-icon'),
            $arrowToggleParentLi: $firstUl.find('a.dd-toggle').parent('li'),
        });

        const navToggleDom = afterSetupVars.$navToggle?.get(0);
        if (!navToggleDom) throw new TypeError('Could not find nav toggle');
        afterSetupVars.toggleTooltip = new CustomTooltip(navToggleDom, {
            title: getToggleTooltipText(),
            placement: oppPosition,
        });
        afterSetupVars.$navToggle?.on('keydown', anchorTagKeydownFixListener);
        $firstUl.css(positionAlignment, '0');
        closedNavBodyGap.set(`var(${navMenuWidth.getVariableName()})`);
        $body.addClass('expand-shrink-enabled');

        afterSetupVars.overlayScrollbarsInstance = OverlayScrollbarsGlobal.OverlayScrollbars(
            {
                target: navListDom,
                elements: {
                    viewport: navListDom,
                },
            },
            {
                scrollbars: {
                    autoHide: 'leave',
                },
            },
        );
    };

    const updateToggleTooltipText = () => {
        if (!afterSetupVars.toggleTooltip) throw new TypeError('toggleTooltip is undefined');
        afterSetupVars.toggleTooltip.hide();
        afterSetupVars.toggleTooltip.setTitle(getToggleTooltipText());
    };

    const temporaryExpandMenu = () => {
        navMenuWidth.reset();
        const oneSecond = 1_000;

        setTimeout(
            () => {
                if (!(hasMenuTempExpanded || isMenuExpanded)) return;
                $elementsToHide.css('display', '');
            },
            Number(
                getComputedStyle(rootElement).getPropertyValue('--expand-shrink-transition-duration').replace('s', ''),
            ) * oneSecond,
        );
        $firstUl.addClass('looks-open');
    };

    const commonRender = () => {
        if (isMenuExpanded) {
            temporaryExpandMenu();
        } else {
            if (afterSetupVars.$arrowToggleParentLi) {
                afterSetupVars.$arrowToggleParentLi.removeClass('open');
                afterSetupVars.$arrowToggleParentLi.find('> ul').css('display', 'none');
            }
            navMenuWidth.set(`${closedNavWidth}px`);
            $elementsToHide.css('display', 'none');
            $firstUl.removeClass('looks-open');
            $body.removeClass('open');
        }
    };

    const nonTemporaryRender = () => {
        if (!afterSetupVars.$toggleIcon) throw new TypeError('$toggleIcon is not defined');
        if (isMenuExpanded) {
            $body.addClass('open');
            afterSetupVars.$toggleIcon.addClass('reverse');
        } else {
            afterSetupVars.$toggleIcon.removeClass('reverse');
        }
        updateToggleTooltipText();
    };

    const shouldExpandMenu = () => {
        if (isMenuExpanded) return false;
        isMenuExpanded = true;
        return true;
    };
    const shouldShrinkMenu = () => {
        if (!isMenuExpanded) return false;
        isMenuExpanded = false;
        return true;
    };

    /** @param {(this: HTMLElement, event: JQuery.ClickEvent) => void} onToggleClick - Handler for when the toggle button is clicked */
    const bindCommonEvents = (onToggleClick) => {
        if (!afterSetupVars.$navToggle) throw new TypeError('Could not bind events. $navToggle is undefined');
        afterSetupVars.$navToggle.on('click', onToggleClick);
    };

    /** @param {(this: HTMLElement, event: JQuery.ClickEvent) => void} onToggleClick - Handler for when the toggle button is clicked */
    const unbindCommonEvents = (onToggleClick) => {
        if (!afterSetupVars.$navToggle) throw new TypeError('Could not unbind events. $navToggle is undefined');
        afterSetupVars.$navToggle.off('click', onToggleClick);
    };

    const commonDestroy = () => {
        if (!afterSetupVars.overlayScrollbarsInstance)
            throw new TypeError('Could not destroy scrollbar. overlayScrollbarsInstance is undefined');
        afterSetupVars.overlayScrollbarsInstance.destroy();
        delete afterSetupVars.overlayScrollbarsInstance;
        $disabledNavLinks.get().forEach((disabledLink) => {
            disabledLink.textContent = disabledLink.dataset.disabledText || '';
            delete disabledLink.dataset.disabledText;
        });
        $firstUl.find('.expand-shrink__toggle-container').remove();
        $firstUl.css(positionAlignment, originalAlignment);
        closedNavBodyGap.reset();
        $body.removeClass('expand-shrink-enabled');

        // Render Artifacts
        navMenuWidth.reset();
        $firstUl.removeClass('looks-open');
        $elementsToHide.css('display', '');

        afterSetupVars.toggleTooltip?.dispose();
        commonFixedHorizontalModule.destroy();
    };

    const expandShrinkMain = (() => {
        /** @type {CustomTooltip[]} */
        let tooltipInstances = [];
        let isModuleSetup = false;

        const render = () => {
            commonRender();
            nonTemporaryRender();
            if (isMenuExpanded) {
                tooltipInstances.forEach((instance) => {
                    instance.dispose();
                });
                tooltipInstances = [];
                return;
            }

            $firstLevelLinks.each((_, element) => {
                const nameElement = element.querySelector('.navDisplayName');
                if (!nameElement) {
                    console.warn("Could not find a tooltip's display name.");
                    return;
                }
                tooltipInstances.push(
                    new CustomTooltip(element, {
                        title: nameElement.textContent,
                        placement: oppPosition,
                    }),
                );
            });
        };

        const expandMenu = () => {
            if (!shouldExpandMenu()) return;
            render();
        };

        const shrinkMenu = () => {
            if (!shouldShrinkMenu()) return;
            render();
        };

        const toggleMenu = () => {
            if (isMenuExpanded) {
                shrinkMenu();
            } else {
                expandMenu();
            }
        };

        const setup = () => {
            if (isModuleSetup) return;
            isModuleSetup = true;
            commonSetup();
            bindCommonEvents(toggleMenu);
            $body.addClass('expand-shrink-main');
            render();
        };

        const destroy = () => {
            if (!isModuleSetup) return;
            isModuleSetup = false;
            commonDestroy();
            unbindCommonEvents(toggleMenu);
            $body.removeClass('expand-shrink-main');
        };

        return { toggleMenu, expandMenu, shrinkMenu, setup, destroy };
    })();

    const hoverExpand = (() => {
        let isModuleSetup = false;

        const render = () => {
            commonRender();
            nonTemporaryRender();
        };

        const tempRender = () => {
            if (hasMenuTempExpanded) {
                temporaryExpandMenu();
                closedNavBodyGap.set(`${closedNavWidth}px`);
            } else {
                render();
            }
        };

        const expandMenu = () => {
            if (!shouldExpandMenu()) return;
            render();
        };

        const shrinkMenu = () => {
            if (!shouldShrinkMenu()) return;
            render();
        };

        const tempExpandMenu = () => {
            if (isMenuExpanded) return;
            if (hasMenuTempExpanded) return;
            hasMenuTempExpanded = true;

            tempRender();
        };

        const tempShrinkMenu = () => {
            if (isMenuExpanded) return;
            if (!hasMenuTempExpanded) return;
            hasMenuTempExpanded = false;

            tempRender();
        };

        const toggleMenu = () => {
            if (isMenuExpanded) {
                shrinkMenu();
            } else {
                expandMenu();
            }
        };

        const toggleTempMenu = () => {
            if (hasMenuTempExpanded) {
                tempShrinkMenu();
            } else {
                tempExpandMenu();
            }
        };

        const bindEvents = () => {
            $firstUl.on('mouseenter', tempExpandMenu);
            $firstUl.on('mouseleave', tempShrinkMenu);
        };
        const unbindEvents = () => {
            $firstUl.off('mouseenter', tempExpandMenu);
            $firstUl.off('mouseleave', tempShrinkMenu);
        };

        const setup = () => {
            if (isModuleSetup) return;
            isModuleSetup = true;

            commonSetup();
            bindCommonEvents(toggleMenu);
            bindEvents();
            $body.addClass('expand-shrink-hover-expand');
            render();
        };

        const destroy = () => {
            if (!isModuleSetup) return;
            isModuleSetup = false;

            commonDestroy();
            unbindCommonEvents(toggleMenu);
            unbindEvents();
            $body.removeClass('expand-shrink-hover-expand');
        };

        return {
            toggleMenu,
            expandMenu,
            shrinkMenu,
            setup,
            destroy,
            toggleTempMenu,
            tempExpandMenu,
            tempShrinkMenu,
        };
    })();

    /**
     * @param {number} index - index of the element
     * @param {HTMLElement} element - the element itself
     */
    const destroyPopupTitle = (index, element) => {
        const $listItem = $(element);

        $listItem.find(`[data-popup-id="${index}"]`).remove();
    };

    /**
     * @param {number} index - The index of the element
     * @param {HTMLElement} listItemDom - The DOM element itself
     */
    const setupPopupTitle = (index, listItemDom) => {
        const $listItem = $(listItemDom);
        const popoverTitle = listItemDom.querySelector('.navDisplayName')?.textContent;
        const $ulDrop = $listItem.find('> ul.drop-nav-wrapper');
        const titleHtml = `
            <div class="expand-shrink__popup-title" data-popup-id="${index}">
                ${popoverTitle}
            </div>`;
        if ($ulDrop.length > 0) {
            $ulDrop.prepend(titleHtml);
            return;
        }
        $listItem.append(
            `<ul class="drop-nav-wrapper expand-shrink__popup">
                ${titleHtml}
            </ul>`,
        );
    };

    const dropOffModule = (() => {
        const $allLinksInUl = $firstUl.find('a.nav-link:not(.disabled)');
        const $allListItemsInUl = $firstUl.find('li');
        /** @type {{[key: number]: JQuery | undefined}} */
        const openedDropdownsByLevel = {};
        /**
         * @param {number} level - The nested level of the dropdown
         * @returns {JQuery} - The Jquery element for the dropdown or undefined
         */
        const getNestedDropdown = (level) => {
            const $chosenDropdown = openedDropdownsByLevel[level];
            if (!$chosenDropdown) throw new TypeError(`The dropdown at level ${level} is undefined.`);
            return $chosenDropdown;
        };

        /**
         * @param {JQuery} $dropdown - The jquery object
         * @returns {HTMLElement} - The first element from the given jquery object
         */
        const getFirstHtml = ($dropdown) => {
            const dropdownDom = $dropdown.get(0);
            if (!dropdownDom) throw new TypeError('The given jquery object was empty.');
            return dropdownDom;
        };

        /**
         * @param {number} level - The nested level of the dropdown
         * @param {JQuery} $newElem - The JQuery dropdown element for the level
         */
        const setNestedDropdown = (level, $newElem) => {
            openedDropdownsByLevel[level] = $newElem;
        };

        /** @param {number} level - The nested level of the dropdown */
        const unsetNestedDropdown = (level) => {
            // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
            delete openedDropdownsByLevel[level];
        };

        const expansionClass = 'expand-shrink__popup--visible';
        const $topHeader = $('.top-header');

        /**
         * @param {number} level - The nested level of the dropdown
         * @returns {boolean} - returns true if the dropdown is open else false
         */
        const checkIfDropdownExists = (level) => {
            const $chosenDropdown = openedDropdownsByLevel[level];

            return $chosenDropdown !== undefined && $chosenDropdown.length > 0;
        };

        /** @param {number} nestedLevel - The nested level of the dropdown */
        const expandDropdown = (nestedLevel) => {
            const $givenDropdown = getNestedDropdown(nestedLevel);
            const givenDropdownDom = getFirstHtml($givenDropdown);
            const $parentLink = $givenDropdown.siblings('a.nav-link');
            const topHeaderHeight = $topHeader.outerHeight() || 0;
            const boundaryGap = 10;
            const boundary = window.innerHeight - topHeaderHeight - boundaryGap;
            const listElemClientRect = getFirstHtml($parentLink.closest('li')).getBoundingClientRect();
            const previousLevel = nestedLevel - 1;

            $givenDropdown.addClass(expansionClass);
            $givenDropdown.css('top', `${listElemClientRect.top}px`);
            $parentLink.addClass('expanded');
            $givenDropdown.css('overflow-y', '');
            $givenDropdown.css('max-height', `${boundary}px`);

            const dropdownClientRect = givenDropdownDom.getBoundingClientRect();

            $givenDropdown.css(
                positionAlignment,
                // Assuming all nested dropdowns will have the same width
                `${Number(navMenuWidth.get().replace('px', '')) + previousLevel * dropdownClientRect.width}px`,
            );

            // if it goes below the body
            if (dropdownClientRect.bottom > window.innerHeight) {
                if (dropdownClientRect.height > boundary) {
                    $givenDropdown.css('top', `${topHeaderHeight}px`);
                    $givenDropdown.css('overflow-y', 'auto');
                } else {
                    $givenDropdown.css('top', 'auto');
                    $givenDropdown.css('bottom', `${boundaryGap}px`);
                }
            }

            // if it goes above the body
            if (dropdownClientRect.top < topHeaderHeight) {
                $givenDropdown.css('top', `${topHeaderHeight}px`);
            }
        };

        /** @param {JQuery} $givenDropdown - The JQuery dropdown to shrink */
        const helperShrinkDropdown = ($givenDropdown) => {
            $givenDropdown.removeClass(expansionClass);
            $givenDropdown.css('top', '');
            $givenDropdown.css('bottom', '');
            $givenDropdown.css('max-height', '');
            $givenDropdown.css('transform', '');
            $givenDropdown.siblings('a.nav-link').removeClass('expanded');
            $givenDropdown.removeData('nested');
        };

        /** @param {number} nestedLevel - The level of the dropdown to shrink */
        const shrinkDropdown = (nestedLevel) => {
            if (!checkIfDropdownExists(nestedLevel)) {
                throw new Error(`The dropdown at the given level (${nestedLevel}) is not open.`);
            }
            let lastLevel = nestedLevel;
            while (checkIfDropdownExists(lastLevel + 1)) {
                lastLevel++;
            }

            for (let i = lastLevel; i >= nestedLevel; i--) {
                helperShrinkDropdown(getNestedDropdown(i));
                unsetNestedDropdown(i);
            }
        };

        const closeAllDropdowns = () => {
            const levelOneId = 1;
            if (!checkIfDropdownExists(levelOneId)) return;
            shrinkDropdown(levelOneId);
        };

        /** @param {JQuery.MouseOverEvent<HTMLElement,undefined,HTMLElement, HTMLElement>} event - The mouseover event object */
        const firstUlCloseDetached = (event) => {
            const secondLevel = 2;
            const mouseLeaveTarget = event.relatedTarget;
            if (
                !(
                    $firstUl.is(event.target) &&
                    mouseLeaveTarget instanceof HTMLElement &&
                    Number(mouseLeaveTarget.dataset.level) === secondLevel
                )
            )
                return;
            if (checkIfDropdownExists(secondLevel)) shrinkDropdown(secondLevel);
        };

        /**
         * @this {HTMLElement}
         * @param {JQuery.MouseOverEvent<HTMLElement,undefined,HTMLElement, HTMLElement>} event - The mouseleave event object
         */
        const closeDetachedDropdowns = function (event) {
            event.stopPropagation();
            const $listItem = $(this);
            const currentLevel = Number($listItem.data('level'));
            const detachedLevel = currentLevel + 1;
            if (!checkIfDropdownExists(detachedLevel)) return;

            // if on the same dropdown then do nothing. example: on a scrollbar
            if (Number($(event.target).parent().data('level')) === currentLevel) return;
            shrinkDropdown(detachedLevel);
        };

        /** @this {HTMLElement} */
        const expandOrHideDropdowns = function () {
            const $linkElem = $(this);
            const nestedLevel = Number($linkElem.closest('li').data('level'));
            const $dropdown = $linkElem.siblings('ul.drop-nav-wrapper');
            const isCurrentLevelDropdownOpen = checkIfDropdownExists(nestedLevel);

            if ($dropdown.length === 0) {
                if (!isCurrentLevelDropdownOpen) return;
                shrinkDropdown(nestedLevel);
                return;
            }
            if (!isCurrentLevelDropdownOpen) {
                setNestedDropdown(nestedLevel, $dropdown);
                expandDropdown(nestedLevel);
                return;
            }
            if (isOrHasDomElement($dropdown, getFirstHtml(getNestedDropdown(nestedLevel)))) {
                return;
            }
            // shrink other dropdown if it is open
            shrinkDropdown(nestedLevel);
            setNestedDropdown(nestedLevel, $dropdown);
            expandDropdown(nestedLevel);
        };

        const bindEvents = () => {
            $firstUl.on('mouseleave', closeAllDropdowns);
            $firstUl.on('mouseover', firstUlCloseDetached);
            $allListItemsInUl.on('mouseover', closeDetachedDropdowns);
            $allLinksInUl.on('mouseenter', expandOrHideDropdowns);
            if (afterSetupVars.$navToggle) {
                afterSetupVars.$navToggle.on('focus', closeAllDropdowns);
                afterSetupVars.$navToggle.on('click', closeAllDropdowns);
            }
        };

        const unbindEvents = () => {
            $firstUl.off('mouseleave', closeAllDropdowns);
            $firstUl.off('mouseover', firstUlCloseDetached);
            $allListItemsInUl.off('mouseover', closeDetachedDropdowns);
            $allLinksInUl.off('mouseenter', expandOrHideDropdowns);
            if (afterSetupVars.$navToggle) {
                afterSetupVars.$navToggle.off('focus', closeAllDropdowns);
                afterSetupVars.$navToggle.off('click', closeAllDropdowns);
            }
        };

        const setup = () => {
            bindEvents();
        };

        const destroy = () => {
            unbindEvents();
        };

        return { setup, destroy };
    })();

    const showPopovers = (() => {
        const $navMainListItems = $firstLevelLinks.parent();
        const $popupsInList = $nav.find('li > ul.drop-nav-wrapper');
        let isModuleSetup = false;

        const render = () => {
            commonRender();
            nonTemporaryRender();
            if (isMenuExpanded) {
                $navMainListItems.each(destroyPopupTitle);
            } else {
                $navMainListItems.each(setupPopupTitle);
            }
        };
        const expandMenu = () => {
            if (!shouldExpandMenu()) return;
            render();
        };
        const shrinkMenu = () => {
            if (!shouldShrinkMenu()) return;
            render();
        };
        const toggleMenu = () => {
            if (isMenuExpanded) {
                shrinkMenu();
            } else {
                expandMenu();
            }
        };

        const setup = () => {
            if (isModuleSetup) return;
            isModuleSetup = true;

            commonSetup();
            dropOffModule.setup();
            bindCommonEvents(toggleMenu);
            $body.addClass('expand-shrink-drop-off');
            $popupsInList.addClass('expand-shrink__popup');
            $popupsInList.each((_index, popup) => {
                popup.dataset.oldStyle = popup.getAttribute('style') || undefined;
            });
            $popupsInList.removeAttr('style');
            render();
        };

        const destroy = () => {
            if (!isModuleSetup) return;
            isModuleSetup = false;

            commonDestroy();
            dropOffModule.destroy();
            $navMainListItems.each(destroyPopupTitle);
            unbindCommonEvents(toggleMenu);
            $body.removeClass('expand-shrink-drop-off');
            $popupsInList.removeClass('expand-shrink__popup');
            $popupsInList.each((_index, popup) => {
                const { oldStyle } = popup.dataset;
                if (oldStyle) popup.setAttribute('style', oldStyle);
                delete popup.dataset.oldStyle;
            });
        };

        return { expandMenu, shrinkMenu, toggleMenu, destroy, setup };
    })();

    return {
        expandShrinkMain,
        hoverExpand,
        showPopovers,
    };
};

export { expandShrinkFactory };
